import {Box, Card, CardActionArea, CardContent, Grid, Button, ButtonGroup} from "@material-ui/core";
import Typography from "@material-ui/core/Typography";
import React, {useState} from "react";
import {makeStyles} from "@material-ui/core/styles";
import BarChart from '@user-components/BarChart';
import PieChart from '@user-components/PieChart';
import { useHistory } from "react-router-dom";
import AuthProvider from "@user-provider/AuthProvider";

/**
 * 뇌기능 내면 상태
 * - BRHW(기본): δ, θ, α, SMR, β, βM, βh (7밴드)
 * - 기본: δ, θ, α, β(SMR+β+βM+βh 합산) (4밴드)
 * 하단에 동일 데이터의 좌/우 퍼센트 파이차트를 표시합니다.
 */
export default function BalanceBar(props) {
    const classes = useStyles();
    const { id, bandRangeData, isEEG } = props.measureResult;
    const history = useHistory()
    const ableClick = true !== props.clickDisabled
    const [mode, setMode] = useState('brhw') // 'brhw' = 7밴드(기본), 'default' = 4밴드

    const handler = () => {
        if(AuthProvider.canViewMeasureDetail() && ableClick) {
            history.push('/measureDetail/' + id + '/balanceBar');
        }
    }

    // BRHW 7밴드
    const get7BandData = () => {
        return {
            labels: ['δ', 'θ', 'α', 'SMR', 'β', 'βM', 'βh'],
            ch1: bandRangeData.ch1.map(v => parseFloat(v)),
            ch2: bandRangeData.ch2.map(v => parseFloat(v))
        }
    }

    // 기본 4밴드: δ, θ, α, β(SMR+β+βM+βh 합산)
    const get4BandData = () => {
        const ch1 = bandRangeData.ch1;
        const ch2 = bandRangeData.ch2;
        return {
            labels: ['δ', 'θ', 'α', 'β'],
            ch1: [
                parseFloat(ch1[0]),
                parseFloat(ch1[1]),
                parseFloat(ch1[2]),
                parseFloat(ch1[3]) + parseFloat(ch1[4]) + parseFloat(ch1[5]) + parseFloat(ch1[6])
            ],
            ch2: [
                parseFloat(ch2[0]),
                parseFloat(ch2[1]),
                parseFloat(ch2[2]),
                parseFloat(ch2[3]) + parseFloat(ch2[4]) + parseFloat(ch2[5]) + parseFloat(ch2[6])
            ]
        }
    }

    const bandData = 'brhw' === mode ? get7BandData() : get4BandData();
    const barDefaultData = [bandData.ch1, bandData.ch2];

    return (
        <Box className={classes.root}>
            <Card className={classes.card}>
                <CardContent>
                    <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
                        <Typography variant={'subtitle1'} className={classes.title}>
                            뇌 기능별 상태
                        </Typography>
                        <ButtonGroup size={'small'} style={{minHeight: 0}}>
                            <Button
                                variant={'brhw' === mode ? 'contained' : 'outlined'}
                                color={'brhw' === mode ? 'primary' : 'default'}
                                onClick={(e) => { e.stopPropagation(); setMode('brhw'); }}
                                style={{fontSize: 12, padding: '4px 6px', lineHeight: 1.4}}
                            >
                                BRHW
                            </Button>
                            <Button
                                variant={'default' === mode ? 'contained' : 'outlined'}
                                color={'default' === mode ? 'primary' : 'default'}
                                onClick={(e) => { e.stopPropagation(); setMode('default'); }}
                                style={{fontSize: 12, padding: '4px 6px', lineHeight: 1.4}}
                            >
                                기본
                            </Button>
                        </ButtonGroup>
                    </Box>

                    <CardActionArea onClick={handler}>
                        <Grid container>
                            <Grid item xs={12}>
                                {'yes' === isEEG &&
                                <>
                                    <BarChart
                                        key={`bar-${mode}-${id}`}
                                        data={new Array(bandData.labels.length).fill(0)}
                                        defaultData={barDefaultData}
                                        showTopValue={true}
                                        topValueMargin={20}
                                        labels={bandData.labels}
                                        height={100}
                                        useColorIndex={true}
                                        showYAxes={true}
                                        colors={['#60C3F7', '#A9D8AA']}
                                        grouped={true}
                                        groups={2}
                                    />
                                    <Box mt={2}>
                                        <Grid container justify={'center'} style={{width: '100%'}}>
                                            {bandData.labels.map((label, index) => {
                                                const ch1 = bandData.ch1[index];
                                                const ch2 = bandData.ch2[index];
                                                const total = ch1 + ch2;
                                                const ch1Percent = 0 === total ? 50 : Math.floor(ch1 / total * 100);
                                                const ch2Percent = 0 === total ? 50 : Math.floor(ch2 / total * 100);

                                                return (
                                                    <Grid item key={`pie-${mode}-${index}-${id}`}>
                                                        <Box textAlign={'center'}>
                                                            <PieChart
                                                                key={`pie-${mode}-${index}-${id}`}
                                                                defaultData={[ch2Percent, ch1Percent]}
                                                                labels={['좌', '우']}
                                                                height={80}
                                                                width={80}
                                                                title={label}
                                                                xLabelDisplay={true}
                                                                useColorIndex={true}
                                                                colors={['#A9D8AA', '#60C3F7']}
                                                            />
                                                            <Typography variant={'caption'} style={{color: '#000', fontSize: 10}}>
                                                                {ch1Percent}% / {ch2Percent}%
                                                            </Typography>
                                                        </Box>
                                                    </Grid>
                                                )
                                            })}
                                        </Grid>
                                    </Box>
                                </>
                                }

                                {'yes' !== isEEG &&
                                <Box display={'flex'} minHeight={200}>
                                    <Box m="auto">
                                        <Typography variant="h6" style={{color:'#999'}}>
                                            뇌기능(EEG) 측정이 필요합니다.
                                        </Typography>
                                    </Box>
                                </Box>
                                }
                            </Grid>
                        </Grid>
                    </CardActionArea>
                </CardContent>
            </Card>
        </Box>
    )
}

const useStyles = makeStyles(theme => ({
    root: {
    },
    title: {
        fontWeight: 'bold',
        paddingLeft: 15,
    },
    card: {
        border: "none",
        boxShadow: "none",
        padding: 0
    },
}));
